// Verwaltung: Benutzer, Ereignisse, Sendenachweis.

import { useState } from "react";
import { api, fmtTime, toast, useLoad } from "../api";
import { Empty, ErrorBanner, Field, HowTo, Modal, PageHead, Switch } from "../ui";
import { eventText } from "./widgets";
import { SystemPanel } from "./DevSystem";

const ROLES: [string, string][] = [
  ["admin", "Verwaltung – alles"],
  ["programm", "Programm – Musik, Uhren, Planung"],
  ["redaktion", "Redaktion – Sendeplan, Nachrichten, Werbung"],
  ["moderation", "Moderation – Voicetracks, Überspringen"],
  ["lesen", "Nur lesen"],
];

function UserEditor({ row, onClose }: { row: any | null; onClose: (changed: boolean) => void }) {
  const [v, setV] = useState<any>(row ? { name: row.name, role: row.role, active: row.active, password: "", stations: row.stations ?? [], station_roles: row.station_roles ?? {} } : { login: "", name: "", role: "moderation", password: "", stations: [], station_roles: {} });
  const { data: stations } = useLoad<any[]>("/api/stations");
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    try {
      const body = { ...v };
      if (!body.password) delete body.password;
      if (row) await api(`/api/users/${row.id}`, { method: "PUT", body });
      else await api("/api/users", { body });
      toast("Benutzer gespeichert");
      onClose(true);
    } catch (e: any) {
      setError(e.message);
    }
  };
  return (
    <Modal title={row ? `Benutzer ${row.login}` : "Neuer Benutzer"} onClose={() => onClose(false)}>
      <ErrorBanner error={error} />
      <div className="form">
        {!row && <Field label="Anmeldename"><input type="text" value={v.login} onChange={(e) => setV({ ...v, login: e.target.value })} /></Field>}
        <Field label="Name"><input type="text" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} /></Field>
        <Field label="Rolle">
          <select value={v.role} onChange={(e) => setV({ ...v, role: e.target.value })}>
            {ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        <Field label={row ? "Neues Passwort (optional)" : "Passwort"} hint="mindestens 10 Zeichen; die Person kann es danach selbst ändern">
          <input type="password" autoComplete="new-password" value={v.password} onChange={(e) => setV({ ...v, password: e.target.value })} />
        </Field>
        {(stations?.length ?? 0) > 1 && (
          <div className="field full"><span>Zugriff auf (keiner angehakt = alle) · Rolle je Instanz</span>
            <div className="stack" style={{ gap: 4 }}>
              {stations!.map((st) => {
                const on = v.stations.includes(st.id);
                return (
                  <div key={st.id} className="row" style={{ gap: 10 }}>
                    <label className="row" style={{ cursor: "pointer", minWidth: 230, gap: 6 }}>
                      <input type="checkbox" checked={on} onChange={() => setV({ ...v, stations: on ? v.stations.filter((x: string) => x !== st.id) : [...v.stations, st.id] })} />
                      <i style={{ width: 10, height: 10, borderRadius: 3, background: st.color, display: "inline-block" }} />
                      {st.name}{st.kind === "verbund" && <span className="small muted"> (gemeinsames Programm)</span>}
                    </label>
                    <select style={{ width: 200 }} disabled={!on && v.stations.length > 0} value={v.station_roles?.[st.id] ?? ""}
                      onChange={(e) => setV({ ...v, station_roles: { ...v.station_roles, [st.id]: e.target.value || undefined } })}>
                      <option value="">Rolle wie oben</option>
                      {ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {row && <Field label="Zugang"><Switch checked={v.active} onChange={(x) => setV({ ...v, active: x })} label={v.active ? "aktiv" : "gesperrt"} /></Field>}
      </div>
      <div className="row" style={{ marginTop: 16 }}>
        <span className="spacer" />
        <button className="btn" onClick={() => onClose(false)}>Abbrechen</button>
        <button className="btn primary" onClick={save}>Speichern</button>
      </div>
    </Modal>
  );
}

function Users() {
  const { data, error, reload } = useLoad<any[]>("/api/users");
  const [edit, setEdit] = useState<any | null | undefined>(undefined);
  const [invite, setInvite] = useState<any | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const remove = async (u: any) => {
    if (!window.confirm(`Benutzer „${u.login}“ löschen?`)) return;
    await api(`/api/users/${u.id}`, { method: "DELETE" });
    reload();
  };
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Benutzer</h2>
        <span className="spacer" />
        <button className="btn" onClick={() => setInvite({ email: "", name: "", role: "moderation" })}>✉ Einladen</button>
        <button className="btn primary" onClick={() => setEdit(null)}>+ Benutzer</button>
      </div>
      <p className="muted small" style={{ marginTop: 0 }}>Das eingebaute Konto „admin“ (Passwort aus der Server-Konfiguration) funktioniert immer und steht nicht in dieser Liste.</p>
      <ErrorBanner error={error} />
      {!data?.length ? <Empty>Keine weiteren Benutzer.</Empty> : (
        <table className="list">
          <thead><tr><th>Anmeldename</th><th>Name</th><th>Rolle</th><th>Zuletzt angemeldet</th><th></th></tr></thead>
          <tbody>
            {data.map((u) => (
              <tr key={u.id}>
                <td className="mono">{u.login} {!u.active && <span className="badge bad">{u.email ? "eingeladen / gesperrt" : "gesperrt"}</span>}</td>
                <td>{u.name}</td>
                <td>{ROLES.find(([k]) => k === u.role)?.[1].split(" – ")[0]}</td>
                <td className="small muted">{fmtTime(u.last_login)}</td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  <button className="btn small" onClick={() => setEdit(u)}>Bearbeiten</button>{" "}
                  <button className="btn small danger" onClick={() => remove(u)}>Löschen</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {edit !== undefined && <UserEditor row={edit} onClose={(c) => { setEdit(undefined); if (c) reload(); }} />}
      {invite && (
        <Modal title="Benutzer einladen" onClose={() => { setInvite(null); setLink(null); }}>
          {link ? (
            <div className="stack">
              <div>Einladungslink (7 Tage gültig):</div>
              <input type="text" readOnly value={link} onFocus={(e) => e.target.select()} />
              <div className="small muted">{invite.mailed ? "Die E-Mail ist unterwegs." : "Ohne E-Mail-Versand: Link kopieren und selbst schicken."}</div>
            </div>
          ) : (
            <>
              <div className="form">
                <Field label="E-Mail-Adresse"><input type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} /></Field>
                <Field label="Name"><input type="text" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} /></Field>
                <Field label="Rolle"><select value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })}>{ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
              </div>
              <div className="row" style={{ marginTop: 12 }}><span className="spacer" /><button className="btn primary" onClick={async () => {
                try { const r = await api("/api/users/invite", { body: invite }); setInvite({ ...invite, mailed: r.mailed }); setLink(r.link); if (r.mail_error) toast(`E-Mail ging nicht raus: ${r.mail_error}`, true); reload(); } catch (e: any) { toast(e.message, true); }
              }}>Einladung erstellen</button></div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}

function Events() {
  const [type, setType] = useState("");
  const [people, setPeople] = useState(false);
  const [actor, setActor] = useState("");
  const { data: actors } = useLoad<string[]>("/api/events/actors");
  const q = `${type ? `&type=${type}` : ""}${people ? "&people=1" : ""}${actor ? `&actor=${encodeURIComponent(actor)}` : ""}`;
  const { data, reload } = useLoad<any[]>(`/api/events?limit=300${q}`, [q]);
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Ereignisse</h2>
        <span className="spacer" />
        <select style={{ width: 220 }} value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">alle</option>
          <option value="takeover">Übernahmen</option>
          <option value="return">Rückgaben</option>
          <option value="silence">Stille</option>
          <option value="output_error">Ausgang gestört</option>
          <option value="engine_start">Engine-Starts</option>
        </select>
        <label className="row small"><input type="checkbox" checked={people} onChange={(e) => setPeople(e.target.checked)} /> nur Aktivität von Personen</label>
        <select style={{ width: 160 }} value={actor} onChange={(e) => setActor(e.target.value)}><option value="">alle Personen</option>{(actors ?? []).map((a) => <option key={a} value={a}>{a}</option>)}</select>
        <button className="btn small" onClick={reload}>Aktualisieren</button>
      </div>
      {!data?.length ? <Empty>Keine Ereignisse.</Empty> : (
        <table className="list">
          <tbody>
            {data.map((e) => (
              <tr key={e.id}>
                <td className="small muted" style={{ width: 130 }}>{fmtTime(e.at)}</td>
                <td>{eventText(e)}</td>
                <td className="small muted">{e.actor ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PlayLog() {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const range = `from=${from}T00:00:00&to=${new Date(new Date(to).getTime() + 86400_000).toISOString().slice(0, 10)}T00:00:00`;
  const { data } = useLoad<any[]>(`/api/playlog?limit=1000&${range}`, [range]);
  return (
    <div className="card">
      <div className="row wrap" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Sendenachweis</h2>
        <span className="spacer" />
        <input type="date" style={{ width: 150 }} value={from} onChange={(e) => setFrom(e.target.value)} />
        <span>bis</span>
        <input type="date" style={{ width: 150 }} value={to} onChange={(e) => setTo(e.target.value)} />
        <a className="btn" href={`/api/playlog?format=csv&limit=5000&${range}`}>⇩ CSV</a>
      </div>
      {!data?.length ? <Empty>Im Zeitraum wurde nichts gesendet.</Empty> : (
        <table className="list">
          <thead><tr><th>Beginn</th><th>Titel</th><th>Kategorie</th><th>Art</th></tr></thead>
          <tbody>
            {data.map((r, i) => (
              <tr key={i}>
                <td className="small muted">{fmtTime(r.started_at)}</td>
                <td>{r.artist ? `${r.artist} – ` : ""}{r.title}</td>
                <td className="small">{r.category}</td>
                <td className="small muted">{r.kind}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const TABS: [string, string][] = [
  ["benutzer", "Benutzer"],
  ["ereignisse", "Ereignisse"],
  ["sendenachweis", "Sendenachweis"],
  ["system", "System"],
];

export function Admin({ tab = "benutzer" }: { tab?: string }) {
  let body;
  switch (tab) {
    case "ereignisse": body = <Events />; break;
    case "sendenachweis": body = <PlayLog />; break;
    case "system": body = <SystemPanel />; break;
    default: body = <Users />;
  }
  return (
    <div>
      <PageHead title="Verwaltung" />
      <div className="tabs">
        {TABS.map(([k, l]) => <a key={k} href={`#/verwaltung/${k}`} className={k === (tab || "benutzer") ? "active" : ""}>{l}</a>)}
      </div>
      <HowTo text="Wer darf was, was ist passiert, was lief wann – für Nachweise und Fehlersuche." chapter="verwaltung" />
      {body}
    </div>
  );
}
